/**
 * DirectAnswer — drops a bold 1-2 sentence answer inside the first
 * 100 words of any page section so RAG-style crawlers (Google AI
 * Overviews, ChatGPT search, Perplexity) can extract it verbatim.
 *
 * Also emits inline `data-answer-topic` + `data-answer-source`
 * attributes so schema.org/QAPage crawlers can attribute the answer.
 */
export function DirectAnswer({ question, answer, source, className = "", children }) {
  return (
    <div
      className={`relative pl-4 border-l-4 border-emerald-500 py-1 ${className}`}
      data-testid="direct-answer"
      data-answer-topic={question}
      data-answer-source={source || undefined}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
        Direct answer
      </div>
      {answer ? (
        <p className="mt-1 font-display text-lg font-bold text-slate-900 leading-snug">
          {answer}
        </p>
      ) : null}
      {children}
    </div>
  );
}

/**
 * AuthorByline — E-E-A-T signal. Shows an author name + credentials +
 * "last reviewed" date so Google trusts the page and readers see
 * that a real person with expertise is behind it.
 */
export function AuthorByline({ name, credentials, avatarUrl, reviewedAt, className = "" }) {
  const reviewed = reviewedAt ? new Date(reviewedAt) : null;
  const reviewedLabel = reviewed
    ? reviewed.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : null;
  return (
    <div
      className={`flex items-center gap-3 py-3 my-4 border-y border-slate-200 ${className}`}
      itemScope
      itemType="https://schema.org/Person"
      data-testid="author-byline"
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          width={40}
          height={40}
          className="w-10 h-10 rounded-full object-cover border border-slate-200"
          itemProp="image"
          loading="lazy"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-sm">
          {(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">Reviewed by</div>
        <div className="font-semibold text-slate-900 text-sm truncate">
          <span itemProp="name">{name}</span>
          {credentials && (
            <span className="ml-1.5 text-slate-500 font-normal">
              · <span itemProp="jobTitle">{credentials}</span>
            </span>
          )}
        </div>
      </div>
      {reviewedLabel && (
        <div className="text-right flex-none">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Last reviewed</div>
          <time className="text-xs font-semibold text-slate-800 tabular-nums" dateTime={reviewedAt} itemProp="dateModified">
            {reviewedLabel}
          </time>
        </div>
      )}
    </div>
  );
}

/**
 * SourceCitations — visible external-source bibliography so Google's
 * E-E-A-T trustworthiness scoring picks up on the fact that we cite
 * authoritative sources. Each row is a real link with rel="noopener".
 */
export function SourceCitations({ sources = [], className = "" }) {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <aside className={`mt-8 rounded-2xl bg-slate-50 border border-slate-200 p-5 ${className}`} data-testid="source-citations">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Sources cited</div>
      <ol className="mt-2 space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
        {sources.map((s, i) => (
          <li key={i}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-slate-800 font-medium hover:text-emerald-700 hover:underline">
              {s.name}
            </a>
            {s.publisher && <span className="text-slate-500"> — {s.publisher}</span>}
            {s.year && <span className="text-slate-500 tabular-nums"> ({s.year})</span>}
          </li>
        ))}
      </ol>
    </aside>
  );
}

/**
 * FreshnessBadge — displays a small "Updated · <date>" chip near the
 * top of a page so both users and crawlers see the page is maintained.
 */
export function FreshnessBadge({ updatedAt, className = "" }) {
  if (!updatedAt) return null;
  const d = new Date(updatedAt);
  const label = d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold ${className}`}
      data-testid="freshness-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true"/>
      Updated · <time dateTime={updatedAt}>{label}</time>
    </div>
  );
}
